import mixpanel from 'mixpanel-browser';

const token = process.env.MIX_MIXPANEL_TOKEN || '';
const debug = process.env.MIX_MIXPANEL_DEBUG === 'true';

if (token && token.length > 0) {
    mixpanel.init(token, {
        debug,
        track_pageview: false, // We handle this manually
        persistence: 'localStorage',
        api_host: 'https://api-eu.mixpanel.com', // EU endpoint
        track_sessions: false, // Disable automatic "Session Ended" events
    });
    window.mixpanel = mixpanel;

    // Generate a session id for client tracking
    const sessionIdKey = '__mx_session_id';
    let sessionId = sessionStorage.getItem(sessionIdKey);
    if (!sessionId) {
        sessionId = Math.random().toString(36).slice(2) + Date.now();
        sessionStorage.setItem(sessionIdKey, sessionId);
    }

    // Derive browser and device info on client for accuracy
    const ua = navigator.userAgent;
    const browser = detectBrowser(ua);
    const deviceType = detectDeviceType(ua);

    const deviceModel = detectDeviceModel(ua);
    
    mixpanel.register({
        'Session ID': sessionId,
        '$browser': browser,
        '$device': deviceType,
        'Device Model': deviceModel,
        '$screen_width': window.screen.width,
        '$screen_height': window.screen.height,
    });

    // Track client-side URL changes (for SPAs or query param changes)
    let lastUrl = location.href;
    
    // Watch for URL changes via pushState/replaceState
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = function() {
        originalPushState.apply(this, arguments);
        urlChanged();
    };
    
    history.replaceState = function() {
        originalReplaceState.apply(this, arguments);
        urlChanged();
    };
    
    // Watch for popstate (back/forward buttons)
    window.addEventListener('popstate', urlChanged);
    
    function urlChanged() {
        const newUrl = location.href;
        if (newUrl !== lastUrl) {
            lastUrl = newUrl;
            
            mixpanel.track('URL Changed', {
                'URL': newUrl,
                'Path': location.pathname,
                'Search': location.search,
                'Hash': location.hash,
                'Session ID': sessionId,
            });
        }
    }

    // Track parameter changes (opt-in via data-track-param)
    document.addEventListener('change', (e) => {
        const target = e.target;
        if (!(target instanceof HTMLElement)) return;
        const param = target.getAttribute('data-track-param');
        if (!param) return;

        const value = target.value ?? null;
        mixpanel.track('Parameter Changed', {
            'Parameter': param,
            'Value': value,
            'Tag': target.tagName,
            'Type': target.type ?? null,
            'Path': location.pathname,
            'Session ID': sessionId,
        });
    });

    // Track clicks (opt-in via data-track-event)
    document.addEventListener('click', (e) => {
        const target = e.target;
        if (!(target instanceof HTMLElement)) return;
        const eventName = target.getAttribute('data-track-event');
        if (!eventName) return;

        mixpanel.track(eventName, {
            'Path': location.pathname,
            'Text': target.innerText?.trim().slice(0, 200) ?? null,
            'Session ID': sessionId,
        });
    });
    
} else {
    // No-op stubs when disabled
    window.mixpanel = {
        track: () => {},
        identify: () => {},
        register: () => {},
        people: {
            set: () => {},
        },
    };
}

export default window.mixpanel;

function bucketDuration(ms) {
    if (ms < 500) return '<500ms';
    if (ms < 1000) return '0.5s-1s';
    if (ms < 2000) return '1s-2s';
    if (ms < 5000) return '2s-5s';
    return '>5s';
}

function detectDeviceType(ua) {
    // Check tablets first (iPads often have 'Mobile' in UA)
    if (/iPad/i.test(ua)) return 'Tablet';
    if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return 'Tablet';
    if (/Tablet/i.test(ua)) return 'Tablet';
    
    // Then check for mobile phones
    if (/iPhone|iPod/i.test(ua)) return 'Mobile';
    if (/Android.*Mobile/i.test(ua)) return 'Mobile';
    if (/Mobile/i.test(ua)) return 'Mobile';
    
    return 'Desktop';
}

function detectDeviceModel(ua) {
    // iPhone models
    if (/iPhone/i.test(ua)) {
        const match = ua.match(/iPhone\s*OS\s*(\d+)[_\.]?(\d+)?/i);
        return match ? `iPhone (iOS ${match[1]})` : 'iPhone';
    }
    
    // iPad models
    if (/iPad/i.test(ua)) {
        const match = ua.match(/CPU\s*OS\s*(\d+)[_\.]?(\d+)?/i);
        return match ? `iPad (iOS ${match[1]})` : 'iPad';
    }
    
    // Android devices
    if (/Android/i.test(ua)) {
        const match = ua.match(/Android[^;]*;\s*([^)]+)\)/i);
        if (match) {
            let model = match[1].trim();
            model = model.replace(/Build\/.*$/i, '').trim();
            if (model && model.length < 50) {
                return model;
            }
        }
        return 'Android Device';
    }
    
    // Windows/Mac desktops
    const winMatch = ua.match(/Windows NT (\d+\.\d+)/i);
    if (winMatch) {
        const versions = {
            '10.0': 'Windows 10/11',
            '6.3': 'Windows 8.1',
            '6.2': 'Windows 8',
            '6.1': 'Windows 7',
        };
        return versions[winMatch[1]] || 'Windows';
    }
    
    const macMatch = ua.match(/Mac OS X (\d+)[_\.]?(\d+)?/i);
    if (macMatch) {
        return `Mac (macOS ${macMatch[1]})`;
    }
    
    return null;
}

function detectBrowser(ua) {
    const matchers = [
        [/Edg\/([\d\.]+)/i, 'Edge '],
        [/OPR\/([\d\.]+)/i, 'Opera '],
        [/Chrome\/([\d\.]+)/i, 'Chrome '],
        [/Firefox\/([\d\.]+)/i, 'Firefox '],
        [/Version\/([\d\.]+).*Safari/i, 'Safari '],
    ];
    for (const [regex, label] of matchers) {
        const m = ua.match(regex);
        if (m) return label + m[1];
    }
    return 'Unknown';
}
